// /opt/isp_erp/frontend/src/components/warehouse/materiales/StockTotalPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Alert, CircularProgress, TablePagination, FormControl, InputLabel, Select,
  MenuItem, FormControlLabel, Switch
} from '@mui/material';
import { Delete, Refresh, Warning } from '@mui/icons-material';
import { warehouseAPI } from '../../../api/warehouse.api';
import {
  Kpi, CampoBusqueda, SelectProveedor, TIPOS_MATERIAL, colorTipo, detalleError, fmtNum, useDebouncedValue, recorte
} from './utils';

const StockTotalPanel = ({ proveedores, ctx }) => {
  const esEncargado = !!ctx && ctx.central === false;
  const puedeFiltrarUbicacion = !!ctx && ctx.central !== false && (ctx.sucursales || []).length > 0;
  const [texto, setTexto] = useState('');
  const busqueda = useDebouncedValue(texto.trim(), 400);
  const [tipo, setTipo] = useState('');
  const [proveedorId, setProveedorId] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [soloBajo, setSoloBajo] = useState(false);
  const [orden, setOrden] = useState('nombre');
  const [direccion, setDireccion] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [bajoTotal, setBajoTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [aEliminar, setAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  // En una sucursal no hay mínimo ni "bajo mínimo": el mínimo es único para todo el sistema
  const vistaSucursal = esEncargado || (ubicacion !== '' && ubicacion !== 'central');
  const verSucursales = !esEncargado && ubicacion === '';
  const verAcciones = !esEncargado && ubicacion === '';
  const nCols = 6 + (verSucursales ? 1 : 0) + (vistaSucursal ? 0 : 2) + (verAcciones ? 1 : 0);

  useEffect(() => {
    setPage(0);
  }, [busqueda]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filtros = {};
      if (busqueda) filtros.q = busqueda;
      if (tipo) filtros.tipo = tipo;
      if (proveedorId !== '') filtros.proveedor_id = proveedorId;
      if (ubicacion && !esEncargado) filtros.ubicacion = ubicacion;
      const [principal, bajo] = await Promise.all([
        warehouseAPI.listarMaterialesPaginado({
          ...filtros, page: page + 1, page_size: rowsPerPage, orden, direccion, solo_bajo_minimo: soloBajo && !vistaSucursal
        }),
        vistaSucursal
          ? Promise.resolve({ data: { total: 0 } })
          : warehouseAPI.listarMaterialesPaginado({ ...filtros, page: 1, page_size: 1, solo_bajo_minimo: true })
      ]);
      setData(principal.data);
      setBajoTotal(bajo.data.total || 0);
    } catch (err) {
      setData({ items: [], total: 0 });
      setBajoTotal(0);
      setError(detalleError(err, 'Error al cargar el stock total'));
    } finally {
      setLoading(false);
    }
  }, [busqueda, tipo, proveedorId, ubicacion, esEncargado, vistaSucursal, soloBajo, orden, direccion, page, rowsPerPage]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const ordenar = (campo) => {
    if (orden === campo) setDireccion(direccion === 'asc' ? 'desc' : 'asc');
    else {
      setOrden(campo);
      setDireccion('asc');
    }
    setPage(0);
  };

  const confirmarEliminar = async () => {
    setEliminando(true);
    try {
      await warehouseAPI.eliminarMaterial(aEliminar.id);
      setAEliminar(null);
      setMensaje('Material eliminado del catálogo');
      setTimeout(() => setMensaje(''), 5000);
      cargar();
    } catch (err) {
      setAEliminar(null);
      setError(detalleError(err, 'No se pudo eliminar el material'));
    } finally {
      setEliminando(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">{esEncargado ? 'Stock de mi sucursal' : 'Stock total de materiales'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {esEncargado
              ? 'Lo que hay en tu sucursal más lo que tienen tus técnicos.'
              : vistaSucursal
                ? 'Lo que hay en la sucursal elegida más lo que tienen sus técnicos.'
                : 'Bodega central, sucursales y técnicos. El estado "bajo mínimo" se calcula en el servidor.'}
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
          <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
            <Kpi titulo="Materiales (según filtros)" valor={data.total} detalle="Solo materiales activos" />
          </Grid>
          {!vistaSucursal && (
            <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
              <Kpi
                titulo="Bajo el mínimo"
                valor={bajoTotal}
                detalle="Stock total igual o menor al mínimo"
                color={bajoTotal > 0 ? 'warning.main' : 'inherit'}
              />
            </Grid>
          )}
        </Grid>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <CampoBusqueda value={texto} onChange={setTexto} label="Buscar por código o nombre" />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select label="Tipo" value={tipo} onChange={(e) => { setTipo(e.target.value); setPage(0); }}>
                  <MenuItem value="">Todos</MenuItem>
                  {TIPOS_MATERIAL.map((t) => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <SelectProveedor value={proveedorId} proveedores={proveedores} onChange={(e) => { setProveedorId(e.target.value); setPage(0); }} />
            </Grid>
            {puedeFiltrarUbicacion && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ubicación</InputLabel>
                  <Select label="Ubicación" value={ubicacion} onChange={(e) => { setUbicacion(e.target.value); setPage(0); }}>
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="central">Bodega central</MenuItem>
                    {ctx.sucursales.map((s) => (<MenuItem key={s.id} value={String(s.id)}>{s.nombre}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={2.5}>
              <Button fullWidth size="small" variant="outlined" startIcon={<Refresh />} onClick={cargar}>Actualizar</Button>
            </Grid>
            {!vistaSucursal && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={soloBajo} onChange={(e) => { setSoloBajo(e.target.checked); setPage(0); }} />}
                  label="Mostrar solo los que están bajo el mínimo"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {mensaje && <Alert severity="success" sx={{ mb: 2 }}>{mensaje}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>
                  <TableSortLabel active={orden === 'codigo'} direction={orden === 'codigo' ? direccion : 'asc'} onClick={() => ordenar('codigo')}>
                    Código y proveedor
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>
                  <TableSortLabel active={orden === 'nombre'} direction={orden === 'nombre' ? direccion : 'asc'} onClick={() => ordenar('nombre')}>
                    Material
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Tipo</TableCell>
                <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}>{vistaSucursal ? 'En la sucursal' : 'En bodega'}</TableCell>
                <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}>En técnicos</TableCell>
                {verSucursales && (<TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}>En sucursales</TableCell>)}
                <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}>
                  <TableSortLabel active={orden === 'stock_total'} direction={orden === 'stock_total' ? direccion : 'asc'} onClick={() => ordenar('stock_total')}>
                    Total
                  </TableSortLabel>
                </TableCell>
                {!vistaSucursal && (<TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}>Mínimo</TableCell>)}
                {!vistaSucursal && (<TableCell align="center" sx={{ bgcolor: '#f5f5f5' }}>Estado</TableCell>)}
                {verAcciones && (<TableCell align="center" sx={{ bgcolor: '#f5f5f5' }}>Acciones</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={nCols} align="center"><CircularProgress size={24} /></TableCell></TableRow>
              ) : data.items.length === 0 ? (
                <TableRow><TableCell colSpan={nCols} align="center">No hay materiales con esos filtros</TableCell></TableRow>
              ) : (
                data.items.map((m) => (
                  <TableRow key={m.id} hover sx={{ bgcolor: m.bajo_minimo ? '#fff8e1' : 'inherit' }}>
                    <TableCell sx={{ minWidth: 140 }}>
                      <Typography variant="body2" fontWeight="bold">{m.codigo}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{m.proveedor || 'Sin proveedor'}</Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Typography variant="body2">{m.nombre}</Typography>
                      {m.observaciones && (
                        <Tooltip title={m.observaciones}>
                          <Typography variant="caption" color="text.secondary" sx={recorte}>{m.observaciones}</Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell><Chip size="small" label={m.tipo} color={colorTipo(m.tipo)} /></TableCell>
                    <TableCell align="right">{fmtNum(m.stock_bodega)}</TableCell>
                    <TableCell align="right">{fmtNum(m.stock_tecnico)}</TableCell>
                    {verSucursales && (<TableCell align="right">{fmtNum(m.stock_sucursales)}</TableCell>)}
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color={m.bajo_minimo ? 'warning.main' : 'inherit'}>
                        {fmtNum(m.stock_total)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{m.unidad}</Typography>
                    </TableCell>
                    {!vistaSucursal && (<TableCell align="right">{fmtNum(m.stock_minimo)}</TableCell>)}
                    {!vistaSucursal && (
                      <TableCell align="center">
                        <Chip
                          size="small"
                          color={m.bajo_minimo ? 'warning' : 'success'}
                          icon={m.bajo_minimo ? <Warning /> : undefined}
                          label={m.bajo_minimo ? 'Bajo mínimo' : 'OK'}
                        />
                      </TableCell>
                    )}
                    {verAcciones && (
                      <TableCell align="center">
                        <Tooltip title={m.stock_total > 0 ? 'Solo se puede eliminar un material sin stock' : 'Eliminar del catálogo'}>
                          <span>
                            <IconButton size="small" color="error" disabled={m.stock_total > 0} onClick={() => setAEliminar(m)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data.total || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          onPageChange={(e, nueva) => setPage(nueva)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </CardContent>

      <Dialog open={!!aEliminar} onClose={() => !eliminando && setAEliminar(null)}>
        <DialogTitle sx={{ bgcolor: '#ffebee', color: '#c62828' }}>Eliminar material del catálogo</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ my: 2 }}>
            El material se desactiva y deja de aparecer en el catálogo. Su historial se conserva.
          </Alert>
          {aEliminar && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="body2"><strong>Código:</strong> {aEliminar.codigo}</Typography>
              <Typography variant="body2"><strong>Nombre:</strong> {aEliminar.nombre}</Typography>
              <Typography variant="body2"><strong>Proveedor:</strong> {aEliminar.proveedor || 'Sin proveedor'}</Typography>
              <Typography variant="body2"><strong>Tipo:</strong> {aEliminar.tipo}</Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAEliminar(null)} disabled={eliminando}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmarEliminar} disabled={eliminando}>
            {eliminando ? 'Eliminando...' : 'Eliminar material'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default StockTotalPanel;
